import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Plus,
  DollarSign,
  TrendingUp,
  Calendar,
  Building2,
  AlertCircle,
  Search,
  Eye,
  Edit,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTRACT_TYPES = {
  ppa: { label: 'Power Purchase Agreement', color: 'bg-sky-100 text-sky-800' },
  msa: { label: 'Master Service Agreement', color: 'bg-purple-100 text-purple-800' },
  sla: { label: 'Service Level Agreement', color: 'bg-amber-100 text-amber-800' },
  service: { label: 'Service Contract', color: 'bg-emerald-100 text-emerald-800' },
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-800',
  draft: 'bg-slate-100 text-slate-800',
};

const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444'];

export default function CommercialPage() {
  const { hasModuleAccess, hasPermission } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newContract, setNewContract] = useState({
    name: '',
    type: 'ppa',
    counterparty: '',
    start_date: '',
    end_date: '',
    value: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeContract, setActiveContract] = useState(null);

  const openView = async (id, edit = false) => {
    try {
      const res = await axios.get(`${API}/commercial/contracts/${id}`);
      setActiveContract(res.data);
      setEditMode(edit);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Failed to load contract', err);
      toast.error('Failed to load contract');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        name: activeContract.name,
        type: activeContract.type,
        counterparty: activeContract.counterparty,
        start_date: activeContract.start_date,
        end_date: activeContract.end_date,
        value: parseFloat(activeContract.value) || 0,
        status: activeContract.status,
      };
      await axios.patch(`${API}/commercial/contracts/${activeContract.id}`, payload);
      setViewDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to update contract', err);
      toast.error(err.response?.data?.detail || 'Failed to update contract');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contractsRes, revenueRes, trendRes] = await Promise.all([
        axios.get(`${API}/commercial/contracts`),
        axios.get(`${API}/commercial/revenue`),
        axios.get(`${API}/commercial/charts/revenue-trend?months=6`).catch(() => ({ data: { series: [] } })),
      ]);
      setContracts(contractsRes.data);
      setRevenue(revenueRes.data);
      setRevenueTrend(
        (trendRes.data.series || []).map((d) => ({
          month: d.month.slice(5), // "2026-04" -> "04"
          revenue: d.revenue,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch commercial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async () => {
    try {
      await axios.post(`${API}/commercial/contracts`, {
        ...newContract,
        value: parseFloat(newContract.value) || 0,
      });
      setCreateDialogOpen(false);
      setNewContract({ name: '', type: 'ppa', counterparty: '', start_date: '', end_date: '', value: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    }
  };

  const filteredContracts = contracts.filter((c) => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.counterparty?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || c.type === filterType;
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const contractTypeData = [
    { name: 'PPA', value: contracts.filter(c => c.type === 'ppa').length },
    { name: 'MSA', value: contracts.filter(c => c.type === 'msa').length },
    { name: 'SLA', value: contracts.filter(c => c.type === 'sla').length },
    { name: 'Service', value: contracts.filter(c => c.type === 'service').length },
  ].filter(d => d.value > 0);

  if (!hasModuleAccess('commercial')) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Module Not Available</h2>
            <p className="text-slate-500">The Commercial module is not enabled for your organization.</p>
            <p className="text-sm text-slate-400 mt-2">Contact your administrator to enable this feature.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="commercial-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Commercial & Revenue</h1>
            <p className="text-slate-500">Contracts, revenue tracking, and commercial analytics</p>
          </div>
          {hasPermission('contract.create') && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-contract-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Contract
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Contract</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Contract Name</Label>
                    <Input
                      placeholder="e.g., North Wind Farm PPA 2026"
                      value={newContract.name}
                      onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
                      data-testid="contract-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contract Type</Label>
                    <Select value={newContract.type} onValueChange={(v) => setNewContract({ ...newContract, type: v })}>
                      <SelectTrigger data-testid="contract-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONTRACT_TYPES).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Counterparty</Label>
                    <Input
                      placeholder="e.g., State Grid Corporation"
                      value={newContract.counterparty}
                      onChange={(e) => setNewContract({ ...newContract, counterparty: e.target.value })}
                      data-testid="contract-counterparty-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={newContract.start_date}
                        onChange={(e) => setNewContract({ ...newContract, start_date: e.target.value })}
                        data-testid="contract-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={newContract.end_date}
                        onChange={(e) => setNewContract({ ...newContract, end_date: e.target.value })}
                        data-testid="contract-end-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Contract Value ($)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 5000000"
                      value={newContract.value}
                      onChange={(e) => setNewContract({ ...newContract, value: e.target.value })}
                      data-testid="contract-value-input"
                    />
                  </div>
                  <Button onClick={handleCreateContract} className="w-full" data-testid="create-contract-submit">
                    Create Contract
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-revenue-mtd">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Revenue MTD</p>
                  <p className="text-3xl font-bold text-slate-900">
                    ${((revenue?.total_revenue_mtd || 0) / 1000000).toFixed(2)}M
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {revenue?.total_revenue_mtd ? '+' : ''}From paid invoices
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-revenue-ytd">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Revenue YTD</p>
                  <p className="text-3xl font-bold text-slate-900">
                    ${((revenue?.total_revenue_ytd || 0) / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {revenue?.total_contracts || 0} total contracts
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-contracts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Contracts</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {contracts.filter(c => c.status === 'active').length}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    ${((revenue?.contracts_value || 0) / 1000000).toFixed(0)}M total value
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-performance">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Performance to Contract</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {revenue?.performance_to_contract || 0}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {revenue?.performance_to_contract >= 90 ? 'On track' : 'Below target'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="contracts">
          <TabsList>
            <TabsTrigger value="contracts" data-testid="tab-contracts">
              <FileText className="w-4 h-4 mr-2" />
              Contracts
            </TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue">
              <TrendingUp className="w-4 h-4 mr-2" />
              Revenue Analytics
            </TabsTrigger>
          </TabsList>

          {/* Contracts Tab */}
          <TabsContent value="contracts" className="mt-6">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search contracts..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="contract-search"
                      />
                    </div>
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[150px]" data-testid="filter-type">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {Object.entries(CONTRACT_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[150px]" data-testid="filter-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Contracts Table */}
            <Card>
              <CardHeader>
                <CardTitle>Contracts</CardTitle>
                <CardDescription>{filteredContracts.length} contracts found</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Contract</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Counterparty</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Value</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Duration</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContracts.map((contract) => (
                          <tr key={contract.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contract-${contract.id}`}>
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{contract.name}</p>
                                <p className="text-sm text-slate-500">{contract.contract_number}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={CONTRACT_TYPES[contract.type]?.color || 'bg-slate-100'}>
                                {CONTRACT_TYPES[contract.type]?.label || contract.type}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600">{contract.counterparty}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900">
                                ${(contract.value / 1000000).toFixed(2)}M
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span>{contract.start_date} - {contract.end_date}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={STATUS_COLORS[contract.status] || 'bg-slate-100'}>
                                {contract.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openView(contract.id, false)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openView(contract.id, true)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredContracts.length === 0 && (
                          <tr>
                            <td colSpan={7}>
                              <EmptyState
                                icon={FileText}
                                title="No contracts found"
                                description={searchTerm ? 'Try adjusting your search or filters' : 'Create your first contract to get started'}
                                actionLabel={!searchTerm ? 'Create Contract' : undefined}
                                onAction={!searchTerm ? () => setCreateDialogOpen(true) : undefined}
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* View / Edit Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={(open) => { if (!open) { setViewDialogOpen(false); setActiveContract(null); setEditMode(false); } }}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editMode ? 'Edit Contract' : 'View Contract'}</DialogTitle>
                </DialogHeader>
                {activeContract && (
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Contract Name</Label>
                      <Input
                        value={activeContract.name}
                        onChange={(e) => setActiveContract({ ...activeContract, name: e.target.value })}
                        readOnly={!editMode}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={activeContract.type} onValueChange={(v) => setActiveContract({ ...activeContract, type: v })} disabled={!editMode}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CONTRACT_TYPES).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Counterparty</Label>
                      <Input value={activeContract.counterparty} onChange={(e)=>setActiveContract(s=>({...s,counterparty:e.target.value}))} readOnly={!editMode} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <Input type="date" value={activeContract.start_date} onChange={(e)=>setActiveContract(s=>({...s,start_date:e.target.value}))} readOnly={!editMode} />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input type="date" value={activeContract.end_date} onChange={(e)=>setActiveContract(s=>({...s,end_date:e.target.value}))} readOnly={!editMode} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Value ($)</Label>
                      <Input type="number" value={activeContract.value} onChange={(e)=>setActiveContract(s=>({...s,value:e.target.value}))} readOnly={!editMode} />
                    </div>
                    <div className="flex gap-2">
                      {editMode ? (
                        <>
                          <Button onClick={handleSaveEdit}>Save</Button>
                          <Button variant="outline" onClick={() => { setEditMode(false); openView(activeContract.id, false); }}>Cancel</Button>
                        </>
                      ) : (
                        <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
                      )}
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Revenue Analytics Tab */}
          <TabsContent value="revenue" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Revenue Trend Chart */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Revenue Trend</CardTitle>
                  <CardDescription>Monthly revenue vs target</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis 
                          stroke="#64748b" 
                          fontSize={12}
                          tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`}
                        />
                        <Tooltip 
                          formatter={(v) => [`$${(v/1000000).toFixed(2)}M`, '']}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#0ea5e9" 
                          fill="#e0f2fe" 
                          name="Revenue"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Contract Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Contract Distribution</CardTitle>
                  <CardDescription>By contract type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={contractTypeData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {contractTypeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue by Contract Type */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle>Revenue by Contract Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                      <p className="text-sm text-sky-600 font-medium">PPA Revenue</p>
                      <p className="text-2xl font-bold text-sky-700">
                        ${((revenue?.revenue_by_contract_type?.ppa || 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-xs text-sky-500 mt-1">73% of total</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-600 font-medium">MSA Revenue</p>
                      <p className="text-2xl font-bold text-purple-700">
                        ${((revenue?.revenue_by_contract_type?.msa || 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-xs text-purple-500 mt-1">18% of total</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-600 font-medium">Spot Sales</p>
                      <p className="text-2xl font-bold text-amber-700">
                        ${((revenue?.revenue_by_contract_type?.spot || 0) / 1000000).toFixed(2)}M
                      </p>
                      <p className="text-xs text-amber-500 mt-1">9% of total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
